import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Star, Eye, Package, Wrench, LogOut, Loader2, BadgeCheck, IndianRupee, CalendarCheck, Pencil, Building2, User, MessageSquare, BriefcaseBusiness, UserRound, CalendarDays, ListChecks, Receipt, UsersRound, CreditCard, History, ReceiptText, BarChart3, Bell, Tag, ShieldCheck, Sparkles, Settings, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/Quote";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { BRAND_LOGO, formatINR } from "@/lib/constants";
import { toast } from "sonner";
import AuthDialog from "@/components/AuthDialog";

export default function VendorDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout, setAuthOpen, enablePushNotifications } = useAuth();
  const [stats, setStats] = useState(null); const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]); const [bookings, setBookings] = useState([]); const [payments, setPayments] = useState([]); const [reviews, setReviews] = useState([]); const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true); const [noProfile, setNoProfile] = useState(false); const [customers, setCustomers] = useState([]);

  const load = () => Promise.all([api.get("/vendor/stats"), api.get("/vendor/me"), api.get("/vendor/leads"), api.get("/vendor/bookings"), api.get("/vendor/payments"), api.get("/vendor/reviews"), api.get("/vendor/quotes")])
    .then(([s, p, l, b, pay, r, q]) => { setStats(s.data); setProfile(p.data); setLeads(l.data); setBookings(b.data); setPayments(pay.data); setReviews(r.data); setQuotes(q.data || []); })
    .catch((e) => { console.error("Vendor Dashboard API failed:", e?.response?.config?.url, e?.response?.status, e?.response?.data || e); setNoProfile(true); }).finally(() => setLoading(false));
  useEffect(() => { if (authLoading) return; if (!user) { setAuthOpen(true); setLoading(false); return; } load(); }, [authLoading, user, setAuthOpen]); // eslint-disable-line

  const setStatus = async (b, status) => {
    try { await api.post(`/bookings/${b.id}/status`, { status }); toast.success(`Booking ${status.replace("_", " ")}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const respond = async (r, text) => { await api.post(`/reviews/${r.id}/respond`, { response: text }); toast.success("Response posted"); load(); };
  const [newDate, setNewDate] = useState("");
  const [sub, setSub] = useState(null);
  const [activeTab, setActiveTab] = useState("leads");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  useEffect(() => {
  if (!user) return;
  Promise.all([
    api.get("/vendor/subscription"),
    api.get("/vendor/subscription/invoices"),
  ])
    .then(([subRes, invoiceRes]) => {
      setSub({
        ...subRes.data,
        invoices: invoiceRes.data?.invoices || [],
      });
    })
    .catch(() => {});
}, [user]);

  const canCustomers = !!sub?.entitlements?.features?.customers;

  useEffect(() => {
    if (!user || !canCustomers) {
      setCustomers([]);
      return;
    }

    api.get("/vendor/customers")
      .then((r) => setCustomers(r.data || []))
      .catch((e) => {
        console.error("Vendor Customers API failed:", e?.response?.status, e?.response?.data || e);
        setCustomers([]);
      });
  }, [user, canCustomers]);

  const subscribe = (slug) => {
    navigate(`/vendor/subscription/checkout?plan=${encodeURIComponent(slug)}`);
  };

  const saveBlocked = async (dates) => { await api.put("/vendor/me", { blocked_dates: dates }); setProfile((p) => ({ ...p, blocked_dates: dates })); };
  const addBlocked = async () => { if (!newDate) return; const cur = profile.blocked_dates || []; if (cur.includes(newDate)) { toast.error("Date already blocked"); return; } try { await saveBlocked([...cur, newDate].sort()); setNewDate(""); toast.success("Date blocked"); } catch (e) { toast.error("Could not block date"); } };
  const removeBlocked = async (d) => { try { await saveBlocked((profile.blocked_dates || []).filter((x) => x !== d)); toast.success("Date unblocked"); } catch (e) { toast.error("Could not unblock"); } };

  if (loading || !profile) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user || noProfile) return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <AuthDialog />
      <div>
        <Building2 className="h-12 w-12 mx-auto text-purple-500 mb-4" />
        <h2 className="font-display font-bold text-xl">{user ? "List your business on MakeMyEventPro" : "Vendor access required"}</h2>
        <p className="text-muted-foreground mt-1">{user ? "Complete the registration wizard and get verified." : "Login with your mobile number."}</p>
        <div className="flex gap-2 justify-center mt-5">
          {user ? <Button data-testid="start-onboarding-btn" onClick={() => navigate("/vendor/onboarding")} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500">Start registration</Button>
            : <Button onClick={() => setAuthOpen(true)} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500">Login</Button>}
          <Button variant="outline" onClick={() => navigate("/")} className="rounded-xl">Marketplace</Button>
        </div>
      </div>
    </div>
  );

  const cards = [
    { icon: Users, label: "Total Leads", value: stats?.total_leads ?? 0, color: "from-blue-500 to-indigo-500" },
    { icon: FileText, label: "New Leads", value: stats?.new_leads ?? 0, color: "from-pink-500 to-rose-500" },
    { icon: CalendarCheck, label: "Bookings", value: stats?.confirmed_bookings ?? 0, color: "from-emerald-500 to-teal-500" },
    { icon: IndianRupee, label: "Earnings", value: formatINR(stats?.earnings ?? 0), color: "from-amber-500 to-orange-500" },
    { icon: Star, label: "Rating", value: `${stats?.rating || 0} ★`, color: "from-purple-500 to-fuchsia-500" },
    { icon: Eye, label: "Profile Views", value: (stats?.profile_views || 0).toLocaleString("en-IN"), color: "from-cyan-500 to-blue-500" },
    { icon: Package, label: "Packages", value: stats?.packages ?? 0, color: "from-violet-500 to-purple-500" },
    { icon: Wrench, label: "Services", value: stats?.services ?? 0, color: "from-slate-600 to-slate-800" },
  ];
  const NEXT = { confirmed: ["in_progress", "cancelled"], in_progress: ["completed"], payment_pending: ["cancelled"] };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-white dark:bg-slate-900 border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2"><img src={BRAND_LOGO} className="h-9 w-9 rounded-xl" alt="" /><span className="font-display font-bold">Vendor Portal</span></button>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl border-0 bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-md hover:from-purple-700 hover:to-pink-600"
                aria-label="Profile"
                title="Profile"
                onClick={() => setProfileMenuOpen((open) => !open)}
              >
                <User className="h-4 w-4" />
              </Button>

              {profileMenuOpen && (
                <div className="absolute right-0 top-12 z-50 w-72 rounded-2xl border border-border bg-white dark:bg-slate-900 shadow-xl overflow-hidden">
                  <div className="px-4 py-4 border-b border-border">
                    <div className="font-display font-bold text-base">{user?.name || "Vendor"}</div>
                    <div className="text-sm text-muted-foreground">
                      {user?.phone || user?.mobile || "Vendor Account"} · vendor
                    </div>
                  </div>

                  <div className="py-2">
                    <button
                      onClick={() => { setProfileMenuOpen(false); navigate("/profile"); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted transition-colors"
                    >
                      <User className="h-5 w-5" /> My Profile
                    </button>

                    <button
                      onClick={() => { setProfileMenuOpen(false); navigate("/vendor/onboarding"); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted transition-colors"
                    >
                      <Pencil className="h-5 w-5" /> Edit Listing
                    </button>

                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        if (profile.slug && profile.status === "approved") {
                          navigate(`/vendor/${profile.slug}`);
                        }
                      }}
                      disabled={!profile.slug || profile.status !== "approved"}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Eye className="h-5 w-5" /> View Public Profile
                    </button>

                    <button
                      onClick={() => { setProfileMenuOpen(false); navigate("/enquiries"); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted transition-colors"
                    >
                      <MessageSquare className="h-5 w-5" /> My Enquiries
                    </button>

                    <button
                      onClick={() => { setProfileMenuOpen(false); navigate("/bookings"); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted transition-colors"
                    >
                      <CalendarCheck className="h-5 w-5" /> My Bookings
                    </button>

                    <button
                      onClick={() => { setProfileMenuOpen(false); navigate("/my-event"); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted transition-colors"
                    >
                      <FileText className="h-5 w-5" /> My Event Planner
                    </button>

                    <button
                      onClick={() => { setProfileMenuOpen(false); navigate("/vendor"); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted transition-colors"
                    >
                      <LayoutDashboard className="h-5 w-5" /> Vendor Dashboard
                    </button>
                  </div>

                  <div className="border-t border-border py-2">
                    <button
                      onClick={() => { setProfileMenuOpen(false); logout(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                    >
                      <LogOut className="h-5 w-5" /> Logout
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl">
          Welcome, <span className="bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">{user.name.split(" ")[0]}</span>
        </h1>
        <p className="text-muted-foreground">{profile.business_name} · {profile.area}, {profile.city}</p>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-foreground">Current Plan:</span>
          <span className="font-bold">{sub?.current?.plan_name || (sub?.expired ? "Expired Plan" : "Free Plan")}</span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
            sub?.current
              ? "bg-emerald-100 text-emerald-700"
              : "bg-red-100 text-red-700"
          }`}>
            {sub?.current ? "Active" : "Inactive"}
          </span>
          {sub?.current?.expiry && (
            <span className="text-muted-foreground">
              · Valid until {new Date(sub.current.expiry).toLocaleDateString("en-IN")}
            </span>
          )}
        </div>

        <div className="rounded-2xl border border-purple-200 bg-purple-50/60 p-5 mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="vendor-push-card">
  <div>
    <h3 className="font-semibold text-foreground">🔔 Enable Notifications</h3>
    <p className="text-sm text-muted-foreground mt-1">
      Get booking updates, enquiries and important MakeMyEventPro alerts.
    </p>
  </div>
  <button
    type="button"
    className="rounded-xl px-5 py-2.5 font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90"
    onClick={async () => {
      try {
        const result = await enablePushNotifications();
        if (result?.ok) {
          toast.success("Notifications enabled successfully");
        } else {
          toast.error("Please allow notifications in your browser");
        }
      } catch (e) {
        toast.error(e?.message || "Unable to enable notifications");
      }
    }}
  >
    Enable Notifications
  </button>
</div>

<div className="rounded-2xl border border-border bg-card p-5 mt-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="vendor-status-card">
          <div className="flex items-center gap-2">
            {profile.verified && profile.status === "approved" ? <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center gap-1"><BadgeCheck className="h-4 w-4" /> Verified Vendor</span> : <StatusBadge status={profile.status} />}
          </div>
          <div className="flex-1"><div className="flex justify-between text-sm mb-1"><span className="font-semibold">Profile completion</span><span className="text-purple-600 font-bold">{stats.profile_completion}%</span></div><Progress value={stats.profile_completion} className="h-2.5" /></div>
          {["draft", "corrections_requested", "rejected"].includes(profile.status) && <Button size="sm" className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500" onClick={() => navigate("/vendor/onboarding")}>{profile.status === "draft" ? "Complete & submit" : "Fix & resubmit"}</Button>}
        </div>
        {profile.admin_note && <div className="rounded-2xl border border-orange-200 bg-orange-50 dark:bg-orange-950/30 p-4 text-sm mt-3"><b>Message from verification team:</b> {profile.admin_note}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {cards.map((c) => <button key={c.label} type="button" onClick={() => {
            const tabs = { "Total Leads": "leads", "New Leads": "leads", "Bookings": "bookings", "Earnings": "payments", "Rating": "reviews", "Packages": "subscription", "Services": "subscription" };
            setActiveTab(tabs[c.label] || "leads");
          }} aria-label={`Open ${c.label}`} className="rounded-2xl border border-border bg-card p-4 text-left cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"><div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-2`}><c.icon className="h-4 w-4 text-white" /></div><div className="text-xs text-muted-foreground">{c.label}</div><div className="font-display font-extrabold text-lg">{c.value}</div></button>)}
        </div>

        <section className="mt-8" data-testid="vendor-business-management">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-extrabold text-lg">Business Management</h2>
              <p className="text-sm text-muted-foreground">Manage your customers, leads, bookings and schedule.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {canCustomers && (
              <button
                type="button"
                onClick={() => setActiveTab("customers")}
                className="text-left rounded-2xl border border-border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-3">
                  <UsersRound className="h-5 w-5 text-white" />
                </div>
                <div className="font-semibold">CRM</div>
                <div className="text-xs text-muted-foreground mt-1">Customers & relationships</div>
              </button>
            )}

            <button
              type="button"
              onClick={() => setActiveTab("leads")}
              className="text-left rounded-2xl border border-border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-3">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div className="font-semibold">Leads</div>
              <div className="text-xs text-muted-foreground mt-1">{leads.length} enquiries</div>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("bookings")}
              className="text-left rounded-2xl border border-border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-3">
                <CalendarCheck className="h-5 w-5 text-white" />
              </div>
              <div className="font-semibold">Bookings</div>
              <div className="text-xs text-muted-foreground mt-1">{bookings.length} bookings</div>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("availability")}
              className="text-left rounded-2xl border border-border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-3">
                <CalendarDays className="h-5 w-5 text-white" />
              </div>
              <div className="font-semibold">Calendar</div>
              <div className="text-xs text-muted-foreground mt-1">Manage availability</div>
            </button>
          </div>
        </section>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="rounded-xl bg-muted flex-wrap h-auto">
            <TabsTrigger value="leads" data-testid="vendor-tab-leads" className="rounded-lg">Leads ({leads.length})</TabsTrigger><TabsTrigger value="quotes" data-testid="vendor-tab-quotes" className="rounded-lg">Quotations ({quotes.length})</TabsTrigger>
            {canCustomers && <TabsTrigger value="customers" data-testid="vendor-tab-customers" className="rounded-lg">Customers ({customers.length})</TabsTrigger>}
            <TabsTrigger value="bookings" data-testid="vendor-tab-bookings" className="rounded-lg">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="payments" data-testid="vendor-tab-payments" className="rounded-lg">Payments</TabsTrigger>
            <TabsTrigger value="availability" data-testid="vendor-tab-availability" className="rounded-lg">Availability</TabsTrigger>
            <TabsTrigger value="subscription" data-testid="vendor-tab-subscription" className="rounded-lg">Subscription</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="vendor-tab-reviews" className="rounded-lg">Reviews</TabsTrigger>
          </TabsList>

          
        <TabsContent value="quotes" className="pt-5">
          <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="vendor-quotations-table">
            {quotes.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Receipt className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No quotations yet.</p>
                <p className="text-sm mt-1">Create quotations from your Leads and they will appear here.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quotation</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((q) => (
                    <TableRow key={q.id} data-testid={`vendor-quote-row-${q.id}`}>
                      <TableCell>
                        <div className="font-semibold">{q.quote_number || "Quotation"}</div>
                        <div className="text-xs text-muted-foreground">
                          {q.created_at ? new Date(q.created_at).toLocaleDateString("en-IN") : "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{q.customer?.name || "Customer"}</div>
                        <div className="text-xs text-muted-foreground">{q.customer?.phone || q.customer?.email || ""}</div>
                      </TableCell>
                      <TableCell>
                        <div>{q.event?.event_type || "-"}</div>
                        <div className="text-xs text-muted-foreground">{q.event?.location || ""}</div>
                      </TableCell>
                      <TableCell>
                        {q.event?.event_date ? new Date(q.event.event_date).toLocaleDateString("en-IN") : "-"}
                      </TableCell>
                      <TableCell className="font-semibold">{formatINR(q.total || 0)}</TableCell>
                      <TableCell><StatusBadge status={q.status} /></TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg"
                          onClick={() => navigate("/vendor/leads/" + q.enquiry_id)}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="leads" className="pt-5">
            <div className="rounded-2xl border border-border bg-card overflow-x-auto" data-testid="vendor-leads-table">
              {leads.length === 0 ? <div className="text-center py-16 text-muted-foreground">No leads yet. They'll appear here when customers enquire.</div> : (
                <Table><TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Event</TableHead><TableHead>Date</TableHead><TableHead>Guests</TableHead><TableHead>Budget</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>{leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="font-medium">{l.customer_name || "Customer"}{l.unread_vendor > 0 && <span className="ml-2 h-5 min-w-5 px-1.5 rounded-full bg-pink-500 text-white text-[11px] font-bold inline-flex items-center justify-center">{l.unread_vendor}</span>}</TableCell>
                      <TableCell>{l.event_type}</TableCell><TableCell>{l.event_date || "—"}</TableCell><TableCell>{l.guests || "—"}</TableCell><TableCell>{l.budget ? formatINR(l.budget) : "—"}</TableCell>
                      <TableCell><StatusBadge status={l.status} /></TableCell>
                      <TableCell><Button data-testid={`lead-open-${l.id}`} size="sm" className="rounded-lg h-8" onClick={() => navigate(`/vendor/leads/${l.id}`)}>Open</Button></TableCell>
                    </TableRow>))}</TableBody></Table>)}
            </div>
          </TabsContent>

          {canCustomers && (
            <TabsContent value="customers" className="pt-5" data-testid="vendor-customers-crm">
              <div className="rounded-2xl border border-border bg-card overflow-x-auto">
                {customers.length === 0 ? (
                  <div className="text-center py-16 text-muted-foreground">
                    No customers yet. Customers will appear here when they enquire or book your services.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Enquiries</TableHead>
                        <TableHead>Bookings</TableHead>
                        <TableHead>Total Booked</TableHead>
                        <TableHead>Total Paid</TableHead>
                        <TableHead>Last Activity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customers.map((c) => (
                        <TableRow key={c.user_id}>
                          <TableCell className="font-medium">{c.name || "Customer"}</TableCell>
                          <TableCell>{c.phone || "—"}</TableCell>
                          <TableCell>{c.enquiries_count || 0}</TableCell>
                          <TableCell>{c.bookings_count || 0}</TableCell>
                          <TableCell>{formatINR(c.total_booked || 0)}</TableCell>
                          <TableCell>{formatINR(c.total_paid || 0)}</TableCell>
                          <TableCell>{c.last_activity ? new Date(c.last_activity).toLocaleDateString("en-IN") : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </TabsContent>
          )}

          <TabsContent value="bookings" className="pt-5">
            <div className="rounded-2xl border border-border bg-card overflow-x-auto" data-testid="vendor-bookings-table">
              {bookings.length === 0 ? <div className="text-center py-16 text-muted-foreground">No bookings yet.</div> : (
                <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Customer</TableHead><TableHead>Event</TableHead><TableHead>Total</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead>Update</TableHead></TableRow></TableHeader>
                  <TableBody>{bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`vendor-booking-${b.id}`}>
                      <TableCell className="font-mono text-xs">{b.code}</TableCell><TableCell>{b.customer_name}<div className="text-xs text-muted-foreground">+91 {b.customer_phone}</div></TableCell>
                      <TableCell>{b.event_type}<div className="text-xs text-muted-foreground">{b.event_date}</div></TableCell><TableCell>{formatINR(b.total)}</TableCell><TableCell className="text-emerald-600">{formatINR(b.paid_amount)}</TableCell>
                      <TableCell><StatusBadge status={b.status} /></TableCell>
                      <TableCell><div className="flex gap-1">{(NEXT[b.status] || []).map((s) => <Button key={s} data-testid={`booking-${s}-${b.id}`} size="sm" variant={s === "cancelled" ? "outline" : "default"} className="rounded-lg h-8 text-xs capitalize" onClick={() => setStatus(b, s)}>{s.replace("_", " ")}</Button>)}</div></TableCell>
                    </TableRow>))}</TableBody></Table>)}
            </div>
          </TabsContent>

          <TabsContent value="payments" className="pt-5">
            <div className="rounded-2xl border border-border bg-card overflow-x-auto" data-testid="vendor-payments-table">
              {payments.length === 0 ? <div className="text-center py-16 text-muted-foreground">No payments yet.</div> : (
                <Table><TableHeader><TableRow><TableHead>Booking</TableHead><TableHead>Type</TableHead><TableHead>Gross</TableHead><TableHead>Commission</TableHead><TableHead>Your earnings</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                  <TableBody>{payments.map((p) => <TableRow key={p.id}><TableCell className="font-mono text-xs">{p.booking_code}</TableCell><TableCell className="capitalize">{p.type}</TableCell><TableCell>{formatINR(p.gross_amount)}</TableCell><TableCell className="text-red-500">-{formatINR(p.commission)}</TableCell><TableCell className="font-bold text-emerald-600">{formatINR(p.vendor_amount)}</TableCell><TableCell><StatusBadge status={p.status} /></TableCell><TableCell className="text-xs">{new Date(p.created_at).toLocaleDateString("en-IN")}</TableCell></TableRow>)}</TableBody></Table>)}
            </div>
          </TabsContent>

          <TabsContent value="availability" className="pt-5">
            <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="vendor-availability">
              <div><h3 className="font-display font-bold">Blocked dates</h3><p className="text-sm text-muted-foreground">Customers cannot book you on blocked dates — availability is enforced on our server.</p></div>
              <div className="flex gap-2 max-w-sm">
                <Input data-testid="blocked-date-input" type="date" value={newDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setNewDate(e.target.value)} className="rounded-xl" />
                <Button data-testid="block-date-btn" className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500" onClick={addBlocked}>Block date</Button>
              </div>
              <div className="flex flex-wrap gap-2">{(profile.blocked_dates || []).length === 0 ? <span className="text-sm text-muted-foreground">No blocked dates.</span> : (profile.blocked_dates || []).map((d) => (
                <span key={d} data-testid={`blocked-${d}`} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted text-sm">{d}<button data-testid={`unblock-${d}`} onClick={() => removeBlocked(d)} className="text-red-500 hover:text-red-600 font-bold">×</button></span>))}</div>
            </div>
          </TabsContent>

          <TabsContent value="subscription" className="pt-5 space-y-4" data-testid="vendor-subscription">
            <div className="rounded-2xl border border-border bg-card p-5">
              {sub?.current ? (
                <div><div className="flex items-center gap-2"><BadgeCheck className="h-5 w-5 text-emerald-600" /><span className="font-display font-bold text-lg">{sub.current.plan_name}</span><span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">Active</span></div>
                  <p className="text-sm text-muted-foreground mt-1">Valid until {new Date(sub.current.expiry).toLocaleDateString("en-IN")} · Gallery up to {sub.entitlements.max_gallery} · Leads {sub.current.lead_limit ?? "∞"}</p></div>
              ) : <div><div className="font-display font-bold">Free plan</div><p className="text-sm text-muted-foreground">{sub?.expired ? "Your subscription expired — renew to restore premium features." : "Upgrade to unlock more gallery images, leads, featured placement and analytics."}</p></div>}
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {(sub?.plans || []).filter(Boolean).map((p) => (
                <div key={p.slug} data-testid={`plan-${p.slug}`} className="rounded-2xl border border-border bg-card p-4">
                  <div className="font-display font-bold text-lg">{p.name}</div>
                  <div className="text-2xl font-extrabold text-purple-600">
  {formatINR(p.price_monthly)}
  <span className="text-xs text-muted-foreground font-normal">
    /{p.validity_days === 365 ? "year" : p.validity_days === 180 ? "6 months" : p.validity_days === 90 ? "3 months" : "30 days"}
  </span>
</div>
                  <ul className="text-sm mt-2 space-y-1">{(p.features || []).map((x) => <li key={x}>• {x}</li>)}</ul>
                  <div className="text-xs text-muted-foreground mt-2">Leads {p.lead_limit ?? "∞"} · Featured slots {p.featured_slots}</div>
                  <Button data-testid={`subscribe-${p.slug}`} className="w-full rounded-xl mt-3 bg-gradient-to-r from-purple-600 to-pink-500" onClick={() => subscribe(p.slug)}>{sub?.current?.plan_slug === p.slug ? "Renew" : "Subscribe"}</Button>
                </div>))}
              {(sub?.plans || []).length === 0 && <p className="text-sm text-muted-foreground">No plans available yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="pt-5 space-y-3" data-testid="vendor-reviews">
            {reviews.length === 0 && <div className="text-center py-16 text-muted-foreground rounded-2xl border border-border bg-card">No reviews yet.</div>}
            {reviews.map((r) => <ReviewRow key={r.id} r={r} onRespond={respond} />)}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ReviewRow({ r, onRespond }) {
  const [t, setT] = useState("");
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex justify-between items-center"><span className="font-semibold">{r.name} · {r.rating}★</span><StatusBadge status={r.status} /></div>
      <p className="text-sm text-muted-foreground mt-1">{r.text}</p>
      {r.response ? <p className="text-sm mt-2 pl-3 border-l-2 border-purple-300"><b>Your reply:</b> {r.response}</p> : (
        <div className="flex gap-2 mt-3"><Textarea value={t} onChange={(e) => setT(e.target.value)} placeholder="Reply publicly…" className="rounded-xl min-h-10" /><Button size="sm" className="rounded-xl" disabled={!t} onClick={() => onRespond(r, t)}>Reply</Button></div>)}
    </div>
  );
}
